// Redaktion: Beiträge, ihre Zustände und der Weg von der Idee bis zur Veröffentlichung.
//
// Ein Beitrag ist – wie eine Seite – eine geprüfte Liste von Blöcken. Dazu kommen
// Titel, Anrisstext, Bild, Kategorien, Autor und ein Zustand.
//
// Wege durch die Zustände:
//   Entwurf ──zur Freigabe──▶ Zur Freigabe ──freigeben──▶ Veröffentlicht
//      │                          │                            │
//      │                   zurück an den Autor            zurückziehen
//      ▼                          ▼                            ▼
//   Geplant ──(Uhrzeit erreicht)──▶ Veröffentlicht          Zurückgezogen
//
// Wer selbst veröffentlichen darf (Leitung, oder Moderation ohne Freigabepflicht),
// springt von „Entwurf“ direkt auf „Veröffentlicht“ oder „Geplant“.
import { z } from "zod";
import { slugifyPage } from "../pages/index.js";
/**
 * Bausteine, die in einem Beitrag stehen dürfen.
 *
 * Ein Beitrag ist ein Text, keine Startseite: Player-Leiste, Sendeplan oder
 * Kontaktformular gehören auf eine Seite, nicht in einen Artikel.
 */
export const POST_BLOCK_KINDS = [
    "text", "gallery", "video", "quote", "episode", "links", "divider", "countdown", "posts",
];
export function isPostBlockKind(kind) {
    return POST_BLOCK_KINDS.includes(kind);
}
export const POST_STATUSES = ["draft", "review", "scheduled", "published", "retracted"];
export const POST_STATUS_LABEL = {
    draft: "Entwurf",
    review: "Zur Freigabe",
    scheduled: "Geplant",
    published: "Veröffentlicht",
    retracted: "Zurückgezogen",
};
export const POST_ACTIONS = ["submit", "approve", "reject", "publish", "schedule", "retract", "reopen"];
export const POST_ACTION_LABEL = {
    submit: "Zur Freigabe geben",
    approve: "Freigeben",
    reject: "Zurück an den Autor",
    publish: "Veröffentlichen",
    schedule: "Erscheinen planen",
    retract: "Zurückziehen",
    reopen: "Wieder bearbeiten",
};
/**
 * Nächster Zustand für eine Aktion – oder eine Begründung, warum das gerade nicht geht.
 * Reine Funktion, damit die Regeln ohne Datenbank prüfbar bleiben.
 */
export function nextPostStatus(current, action, rights) {
    const directly = rights.mayPublish || !rights.reviewRequired;
    switch (action) {
        case "submit":
            if (current === "draft" || current === "retracted")
                return { ok: true, status: "review" };
            return { ok: false, error: "Nur ein Entwurf geht zur Freigabe." };
        case "approve":
            if (!rights.mayPublish)
                return { ok: false, error: "Nur die Redaktionsleitung darf freigeben." };
            if (current === "review")
                return { ok: true, status: "published" };
            return { ok: false, error: "Dieser Beitrag wartet nicht auf Freigabe." };
        case "reject":
            if (!rights.mayPublish)
                return { ok: false, error: "Nur die Redaktionsleitung darf zurückgeben." };
            if (current === "review")
                return { ok: true, status: "draft" };
            return { ok: false, error: "Dieser Beitrag wartet nicht auf Freigabe." };
        case "publish":
            if (!directly)
                return { ok: false, error: "Bitte zur Freigabe geben – veröffentlichen darf die Redaktionsleitung." };
            if (current === "published")
                return { ok: false, error: "Der Beitrag ist schon veröffentlicht." };
            return { ok: true, status: "published" };
        case "schedule":
            if (!directly)
                return { ok: false, error: "Bitte zur Freigabe geben – das Erscheinen plant die Redaktionsleitung." };
            if (current === "published")
                return { ok: false, error: "Der Beitrag ist schon veröffentlicht. Erst zurückziehen." };
            return { ok: true, status: "scheduled" };
        case "retract":
            if (!rights.mayPublish)
                return { ok: false, error: "Nur die Redaktionsleitung darf zurückziehen." };
            if (current === "published" || current === "scheduled")
                return { ok: true, status: "retracted" };
            return { ok: false, error: "Der Beitrag steht gar nicht öffentlich." };
        case "reopen":
            if (current === "published")
                return { ok: false, error: "Erst zurückziehen, dann bearbeiten." };
            return { ok: true, status: "draft" };
        default:
            return { ok: false, error: "Unbekannte Aktion." };
    }
}
/** Welche Zustände auf der Hörerseite zu sehen sind. */
export function isPublicStatus(status) {
    return status === "published";
}
// ---------------------------------------------------------------- Adressen
export const POST_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,78}[a-z0-9])?$/;
export function isPostSlug(value) {
    return POST_SLUG_RE.test(value);
}
/** Aus einem Titel eine Adresse machen; „Eifel-Nacht zieht um“ → „eifel-nacht-zieht-um“. */
export function slugifyPost(title) {
    const base = slugifyPage(title);
    return base === "seite" ? "beitrag" : base;
}
// ---------------------------------------------------------------- Lesezeit
const WORDS_PER_MINUTE = 200;
function countWords(value) {
    if (typeof value !== "string")
        return 0;
    const words = value.trim().split(/\s+/).filter(Boolean);
    return words.length;
}
/** Grobe Lesezeit in Minuten, mindestens 1 – aus Anrisstext und allen Texten der Blöcke. */
export function readingMinutes(teaser, blocks) {
    let words = countWords(teaser);
    for (const block of blocks) {
        for (const value of Object.values(block.props)) {
            words += countWords(value);
        }
    }
    return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}
// ---------------------------------------------------------------- Kategorien
export const CategorySchema = z.object({
    name: z.string().trim().min(1).max(60),
    slug: z.string().trim().max(60).optional(),
});
/**
 * Entwurf aus einer Meldung: Stichpunkte und Quelle als Notiz – nie der fremde
 * Wortlaut als fertiger Text. Das Urheberrecht schützt die Formulierung, nicht
 * die Nachricht selbst; deshalb steht der Hinweis direkt im Entwurf.
 */
export const OWN_WORDS_HINT = "Bitte in eigenen Worten schreiben. Der Wortlaut fremder Meldungen ist urheberrechtlich geschützt – Fakten sind frei.";
export function newsDraftTeaser(seed) {
    const summary = (seed.summary ?? "").trim().replace(/\s+/g, " ");
    return summary.length > 240 ? `${summary.slice(0, 237)}…` : summary;
}
/** Stichpunkte für den Sprechzettel des Beitrags. */
export function newsDraftNotes(seed) {
    const lines = [`## Stichpunkte`, ``, `- ${seed.title.trim()}`];
    const summary = (seed.summary ?? "").trim().replace(/\s+/g, " ");
    if (summary)
        lines.push(`- ${summary}`);
    lines.push("", `**Quelle:** ${seed.source ?? "unbekannt"}${seed.link ? ` – ${seed.link}` : ""}`);
    lines.push("", OWN_WORDS_HINT);
    return lines.join("\n");
}
